import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import request from '@/utils/request';
import API from '@/config/api';
import type { ListParams } from '@/types/api.types';
import type { AddUserRequest, UpdateUserRequest, User } from '@/types/user.types';
import {
  fetchUsersRequest,
  fetchUsersSuccess,
  fetchUsersFailure,
  addUserRequest,
  addUserSuccess,
  addUserFailure,
  updateUserRequest,
  updateUserSuccess,
  updateUserFailure,
  deleteUserRequest,
  deleteUserSuccess,
  deleteUserFailure,
  checkEmailExistRequest,
  checkEmailExistSuccess,
} from './usersSlice';

function* handleFetchUsers(action: PayloadAction<ListParams>) {
  try {
    const { page, limit, search, sortField, sortOrder } = action.payload;
    const params: Record<string, unknown> = { page, limit };
    if (search) params.search = search;
    if (sortField) params.sortField = sortField;
    if (sortOrder) params.sortOrder = sortOrder === 'ascend' ? 1 : -1;

    const response: { data: { data: { list: User[]; totalRecords: number } } } = yield call(
      request.get,
      API.USERS,
      { params },
    );
    yield put(
      fetchUsersSuccess({
        users: response.data.data.list,
        total: response.data.data.totalRecords,
      }),
    );
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to fetch users';
    yield put(fetchUsersFailure(message));
  }
}

function* handleAddUser(action: PayloadAction<AddUserRequest>) {
  try {
    yield call(request.post, API.ADD_USER, action.payload);
    yield put(addUserSuccess());
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to add user';
    yield put(addUserFailure(message));
  }
}

function* handleUpdateUser(action: PayloadAction<UpdateUserRequest>) {
  try {
    const { _id, ...data } = action.payload;
    yield call(request.put, API.USER(_id), data);
    yield put(updateUserSuccess());
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update user';
    yield put(updateUserFailure(message));
  }
}

function* handleDeleteUser(action: PayloadAction<string>) {
  try {
    yield call(request.delete, API.USER(action.payload));
    yield put(deleteUserSuccess(action.payload));
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to delete user';
    yield put(deleteUserFailure(message));
    
  }
}

function* handleCheckEmailExist(action: PayloadAction<string>) {
  try {
    const response: { data: { data: boolean } } = yield call(
      request.get,
      API.EMAIL_EXIST(action.payload),
    );
    yield put(checkEmailExistSuccess(response.data.data));
  } catch {
    yield put(checkEmailExistSuccess(false));
  }
}

export default function* usersSaga() {
  yield takeLatest(fetchUsersRequest.type, handleFetchUsers);
  yield takeLatest(addUserRequest.type, handleAddUser);
  yield takeLatest(updateUserRequest.type, handleUpdateUser);
  yield takeLatest(deleteUserRequest.type, handleDeleteUser);
  yield takeLatest(checkEmailExistRequest.type, handleCheckEmailExist);
}
